import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { assignmentsSeenKey } from '@/lib/deviceProgressState';

interface NewAssignment {
  id: string;
  lesson_id: string;
  assigned_at: string;
  team_name: string;
}

export function useNewAssignments() {
  const { user } = useAuth();
  const [newAssignments, setNewAssignments] = useState<NewAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const getSeenAt = useCallback(() => {
    try {
      return localStorage.getItem(assignmentsSeenKey(user?.id)) || '1970-01-01T00:00:00Z';
    } catch {
      return '1970-01-01T00:00:00Z';
    }
  }, [user?.id]);

  const markAllSeen = useCallback(() => {
    try { localStorage.setItem(assignmentsSeenKey(user?.id), new Date().toISOString()); } catch { /* ignore */ }
    setNewAssignments([]);
  }, [user?.id]);


  const fetchNewAssignments = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const seenAt = getSeenAt();

    // Get player's teams
    const { data: memberships } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('player_user_id', user.id);

    if (!memberships || memberships.length === 0) {
      setLoading(false);
      return;
    }

    const teamIds = memberships.map(m => m.team_id);

    // Get assignments newer than last seen
    const { data: assignments } = await supabase
      .from('lesson_assignments')
      .select('id, lesson_id, assigned_at, team_id')
      .in('team_id', teamIds)
      .gt('assigned_at', seenAt)
      .order('assigned_at', { ascending: false });

    if (!assignments || assignments.length === 0) {
      setNewAssignments([]);
      setLoading(false);
      return;
    }

    // Get team names
    const { data: teams } = await supabase
      .from('teams')
      .select('id, name')
      .in('id', teamIds);

    const teamMap = new Map((teams || []).map(t => [t.id, t.name]));

    setNewAssignments(
      assignments.map(a => ({
        id: a.id,
        lesson_id: a.lesson_id,
        assigned_at: a.assigned_at,
        team_name: teamMap.get(a.team_id) || 'Your team',
      }))
    );
    setLoading(false);
  }, [user, getSeenAt]);

  useEffect(() => { fetchNewAssignments(); }, [fetchNewAssignments]);

  // Realtime: listen for new inserts
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('assignment-notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'lesson_assignments' },
        () => {
          // Refetch to pick up new assignments (RLS will filter to player's teams)
          fetchNewAssignments();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchNewAssignments]);

  return { newAssignments, loading, markAllSeen };
}
